import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, TextFieldElement, SelectElement, SwitchElement } from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchAgentValidations } from "../dataaccess/fetchAgentValidations";
import { createValidationAfterEdit } from "../dataaccess/createValidationAfterEdit";
import { updateValidationAfterEdit } from "../dataaccess/updateValidationAfterEdit";
import { ValidationOperator } from "../API";

const validationSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  operator: z.nativeEnum(ValidationOperator),
  expectedValue: z.string().optional(),
  customPromptTemplate: z.string().optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).default("ACTIVE"),
});

type ValidationFormData = z.infer<typeof validationSchema>;

interface ValidationTabProps {
  agentId: string;
  projectId: string;
}

export const ValidationTab: React.FC<ValidationTabProps> = ({
  agentId,
  projectId,
}) => {
  const [editingValidation, setEditingValidation] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, watch } = useForm<ValidationFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      operator: ValidationOperator.SHOULD_CONTAIN,
      status: "ACTIVE",
    },
  });

  const selectedOperator = watch("operator");

  // Fetch validations for this agent
  const {
    data: validationsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["agent-validations", agentId],
    queryFn: () => fetchAgentValidations(agentId),
    enabled: !!agentId,
  });

  // Create validation mutation
  const createMutation = useMutation({
    mutationFn: createValidationAfterEdit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-validations", agentId] });
      setDialogOpen(false);
      reset();
    },
  });

  // Update validation mutation
  const updateMutation = useMutation({
    mutationFn: updateValidationAfterEdit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-validations", agentId] });
      setDialogOpen(false);
      setEditingValidation(null);
      reset();
    },
  });

  const validations = validationsData?.data?.validationsByAgentId?.items || [];

  const handleOpenDialog = (validation?: any) => {
    if (validation) {
      setEditingValidation(validation);
      reset({
        name: validation.name || "",
        operator: validation.operator,
        expectedValue: validation.expectedValue || "",
        customPromptTemplate: validation.customPromptTemplate || "",
        status: validation.status || "ACTIVE",
      });
    } else {
      setEditingValidation(null);
      reset({
        operator: ValidationOperator.SHOULD_CONTAIN,
        status: "ACTIVE",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingValidation(null);
    reset();
  };

  const onSubmit = (data: ValidationFormData) => {
    if (editingValidation) {
      updateMutation.mutate({
        id: editingValidation.id,
        ...data,
        name: data.name || "", // Don't generate default name
      });
    } else {
      createMutation.mutate({
        projectId,
        agentId,
        ...data,
        name: data.name || "", // Don't generate default name
      });
    }
  };

  const handleDelete = (validation: any) => {
    // Soft delete by updating status
    updateMutation.mutate({
      id: validation.id,
      status: "DELETED",
    });
  };

  const handleToggleStatus = (validation: any) => {
    // Toggle between ACTIVE and INACTIVE
    const newStatus = validation.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updateMutation.mutate({
      id: validation.id,
      status: newStatus,
    });
  };

  const getOperatorDescription = (operator: ValidationOperator) => {
    switch (operator) {
      case ValidationOperator.SHOULD_CONTAIN:
        return "Response should contain the expected text";
      case ValidationOperator.SHOULD_NOT_CONTAIN:
        return "Response should NOT contain the expected text";
      case ValidationOperator.SHOULD_RUN_TOOL:
        return "Agent should use the specified tool";
      case ValidationOperator.SHOULD_NOT_RUN_TOOL:
        return "Agent should NOT use the specified tool";
      case ValidationOperator.CUSTOM_LLM_JUDGE:
        return "Use custom LLM prompt to judge response quality";
      default:
        return "";
    }
  };

  const getOperatorColor = (operator: ValidationOperator) => {
    switch (operator) {
      case ValidationOperator.SHOULD_CONTAIN:
      case ValidationOperator.SHOULD_RUN_TOOL:
        return "success";
      case ValidationOperator.SHOULD_NOT_CONTAIN:
      case ValidationOperator.SHOULD_NOT_RUN_TOOL:
        return "warning";
      case ValidationOperator.CUSTOM_LLM_JUDGE:
        return "info";
      default:
        return "default";
    }
  };

  const generateDefaultName = (operator: ValidationOperator, expectedValue?: string): string => {
    if (!expectedValue) {
      switch (operator) {
        case ValidationOperator.SHOULD_CONTAIN:
          return "Should contain text";
        case ValidationOperator.SHOULD_NOT_CONTAIN:
          return "Should not contain text";
        case ValidationOperator.SHOULD_RUN_TOOL:
          return "Should use tool";
        case ValidationOperator.SHOULD_NOT_RUN_TOOL:
          return "Should not use tool";
        case ValidationOperator.CUSTOM_LLM_JUDGE:
          return "Custom LLM evaluation";
        default:
          return "Validation rule";
      }
    }

    // If expectedValue is short (<=30 chars), include it in the name
    if (expectedValue.length <= 30) {
      switch (operator) {
        case ValidationOperator.SHOULD_CONTAIN:
          return `Should contain "${expectedValue}"`;
        case ValidationOperator.SHOULD_NOT_CONTAIN:
          return `Should not contain "${expectedValue}"`;
        case ValidationOperator.SHOULD_RUN_TOOL:
          return `Should use ${expectedValue}`;
        case ValidationOperator.SHOULD_NOT_RUN_TOOL:
          return `Should not use ${expectedValue}`;
        case ValidationOperator.CUSTOM_LLM_JUDGE:
          return "Custom LLM evaluation";
        default:
          return "Validation rule";
      }
    }

    // If expectedValue is long, use generic name
    return generateDefaultName(operator);
  };

  if (isLoading) {
    return <Typography>Loading validations...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error loading validations</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Validation
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Create validation rules to automatically test your agent's responses and detect behavioral drift.
        Think of these as unit tests for AI responses.
      </Typography>

      <List>
        {validations
          .filter((v: any) => v.status === "ACTIVE")
          .map((validation: any) => (
            <ListItem key={validation.id} sx={{ px: 0 }}>
              <Card sx={{ width: "100%" }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label={validation.operator}
                          size="small"
                          color={getOperatorColor(validation.operator)}
                        />
                        <Typography variant="h6">
                          {validation.name && validation.name.trim() !== "" 
                            ? validation.name 
                            : validation.expectedValue || getOperatorDescription(validation.operator)
                          }
                        </Typography>
                      </Box>
                      
                      {validation.description && (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          {validation.description}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" mb={1}>
                        {getOperatorDescription(validation.operator)}
                      </Typography>
                      
                      {validation.expectedValue && (
                        <Typography variant="body2">
                          <strong>Expected:</strong> {validation.expectedValue}
                        </Typography>
                      )}
                      
                      {validation.customPromptTemplate && (
                        <Typography variant="body2">
                          <strong>Custom Prompt:</strong> {validation.customPromptTemplate}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box display="flex" gap={1} alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={validation.status === "ACTIVE"}
                            onChange={() => handleToggleStatus(validation)}
                            size="small"
                            disabled={updateMutation.isPending}
                          />
                        }
                        label=""
                      />
                      <IconButton onClick={() => handleOpenDialog(validation)} size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(validation)} 
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </ListItem>
          ))}
      </List>

      {validations.filter((v: any) => v.status === "ACTIVE").length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          No validation rules configured. Click "Add Validation" to create your first rule.
        </Typography>
      )}

      {/* Inactive Validations Section */}
      {validations.filter((v: any) => v.status === "INACTIVE").length > 0 && (
        <Box mt={4}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Inactive Validations ({validations.filter((v: any) => v.status === "INACTIVE").length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {validations
                  .filter((v: any) => v.status === "INACTIVE")
                  .map((validation: any) => (
                    <ListItem key={validation.id} sx={{ px: 0 }}>
                      <Card sx={{ width: "100%", opacity: 0.7 }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip
                                  label={validation.operator}
                                  size="small"
                                  color={getOperatorColor(validation.operator)}
                                />
                                <Typography variant="h6">
                                  {validation.name && validation.name.trim() !== "" 
                                    ? validation.name 
                                    : validation.expectedValue || getOperatorDescription(validation.operator)
                                  }
                                </Typography>
                              </Box>
                              
                              {validation.description && (
                                <Typography variant="body2" color="text.secondary" mb={1}>
                                  {validation.description}
                                </Typography>
                              )}
                              
                              <Typography variant="body2" mb={1}>
                                {getOperatorDescription(validation.operator)}
                              </Typography>
                              
                              {validation.expectedValue && (
                                <Typography variant="body2">
                                  <strong>Expected:</strong> {validation.expectedValue}
                                </Typography>
                              )}
                              
                              {validation.customPromptTemplate && (
                                <Typography variant="body2">
                                  <strong>Custom Prompt:</strong> {validation.customPromptTemplate}
                                </Typography>
                              )}
                            </Box>
                            
                            <Box display="flex" gap={1} alignItems="center">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={validation.status === "ACTIVE"}
                                    onChange={() => handleToggleStatus(validation)}
                                    size="small"
                                    disabled={updateMutation.isPending}
                                  />
                                }
                                label=""
                              />
                              <IconButton onClick={() => handleOpenDialog(validation)} size="small">
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                onClick={() => handleDelete(validation)} 
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </ListItem>
                  ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Validation Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingValidation ? "Edit Validation Rule" : "Add Validation Rule"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                <SelectElement
                  control={control}
                  name="operator"
                  label="Validation Type"
                  fullWidth
                  size="small"
                  options={[
                    { id: ValidationOperator.SHOULD_CONTAIN, label: "Should Contain Text" },
                    { id: ValidationOperator.SHOULD_NOT_CONTAIN, label: "Should NOT Contain Text" },
                    { id: ValidationOperator.SHOULD_RUN_TOOL, label: "Should Use Tool" },
                    { id: ValidationOperator.SHOULD_NOT_RUN_TOOL, label: "Should NOT Use Tool" },
                    { id: ValidationOperator.CUSTOM_LLM_JUDGE, label: "Custom LLM Judge" },
                  ]}
                />
              </Grid>
              {(selectedOperator === ValidationOperator.SHOULD_CONTAIN ||
                selectedOperator === ValidationOperator.SHOULD_NOT_CONTAIN) && (
                <Grid item xs={12}>
                  <TextFieldElement
                    control={control}
                    name="expectedValue"
                    label="Expected Text"
                    fullWidth
                    size="small"
                    placeholder="Text that should/shouldn't appear in responses"
                  />
                </Grid>
              )}
              {(selectedOperator === ValidationOperator.SHOULD_RUN_TOOL ||
                selectedOperator === ValidationOperator.SHOULD_NOT_RUN_TOOL) && (
                <Grid item xs={12}>
                  <TextFieldElement
                    control={control}
                    name="expectedValue"
                    label="Tool Name"
                    fullWidth
                    size="small"
                    placeholder="Name of the tool (e.g., 'search', 'calculator')"
                  />
                </Grid>
              )}
              {selectedOperator === ValidationOperator.CUSTOM_LLM_JUDGE && (
                <Grid item xs={12}>
                  <TextFieldElement
                    control={control}
                    name="customPromptTemplate"
                    label="Custom Evaluation Prompt"
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                    placeholder="Custom prompt to evaluate the response. Use {{response}} to reference the agent's response."
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextFieldElement
                  control={control}
                  name="name"
                  label="Name (optional)"
                  fullWidth
                  size="small"
                  placeholder="Leave blank to auto-generate from validation type"
                />
              </Grid>
              <Grid item xs={12}>
                <SelectElement
                  control={control}
                  name="status"
                  label="Status"
                  fullWidth
                  size="small"
                  options={[
                    { id: "ACTIVE", label: "Active (validation will run)" },
                    { id: "INACTIVE", label: "Inactive (validation disabled)" },
                  ]}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingValidation ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};
